import { AlertTriangle, History } from "lucide-react";
import { SelectInput } from "@/components/ExcelUI";

const FIELD_LABELS = { personeel: "medewerkers", personeelBureaus: "uitzendbureaus", personeelUren: "personeelsplanning (uren)", vakanties: "afwezigheden", permissions: "rechten", entries: "productieregels", halffabRuns: "halffabricaat-runs", expeditie: "expeditie/inboekingen", planning: "productieplanning", products: "producten", lines: "lijnen", rawMaterials: "grondstoffen", helpers: "hulpstoffen", klanten: "klanten", recepten: "recepten", leveranciers: "leveranciers", maandData: "maandcijfers (W&V)", costs: "kosteninstellingen" };

export function ConflictModal({ conflict, onReload, onOverwrite }) {
  if (!conflict) return null;
  if (conflict.kind === "blocked") {
    const velden = (conflict.fields || []).map((f) => FIELD_LABELS[f] || f).join(", ");
    const bulk = conflict.reason === "bulk_change_blocked" || conflict.reason === "bulk_permission_downgrade_blocked";
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="conflict-modal">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <div className="flex items-center gap-2 mb-2 text-amber-700 font-bold"><AlertTriangle className="h-5 w-5" /> Opslaan geblokkeerd ter bescherming</div>
          <p className="text-sm text-slate-600 mb-2" data-testid="conflict-blocked-text">
            Dit is <strong>geen</strong> collega-conflict. De server weigerde deze opslag omdat die {bulk ? "in één keer veel gegevens zou wijzigen of verwijderen" : "gevulde gegevens zou leegmaken"}{velden ? <>: <strong>{velden}</strong></> : ""}.
            Er is niets overschreven; de cloudversie is ongewijzigd en een veiligheidskopie is gemaakt.
          </p>
          <p className="text-xs text-slate-500 mb-5">Oorzaak is meestal een verouderd scherm. Laad de cloudversie en voer je laatste wijziging daarna opnieuw in.</p>
          <button data-testid="conflict-reload" onClick={onReload} className="w-full rounded-md bg-[#1C64B0] hover:bg-[#154E8A] text-white font-semibold px-4 py-2 text-sm">Cloudversie laden</button>
        </div>
      </div>
    );
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="conflict-modal">
      <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
        <div className="flex items-center gap-2 mb-2 text-rose-700 font-bold"><AlertTriangle className="h-5 w-5" /> Gelijktijdige bewerking</div>
        <p className="text-sm text-slate-600 mb-5">{conflict.by || "Een collega"} heeft de gegevens ondertussen gewijzigd{conflict.at ? ` (${new Date(conflict.at).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })})` : ""}. Kies wat er met jouw wijzigingen moet gebeuren:</p>
        <div className="flex flex-col gap-2">
          <button data-testid="conflict-reload" onClick={onReload} className="rounded-md bg-[#1C64B0] hover:bg-[#154E8A] text-white font-semibold px-4 py-2 text-sm">Hun versie laden (mijn wijzigingen verwerpen)</button>
          <button data-testid="conflict-overwrite" onClick={onOverwrite} className="rounded-md border border-rose-300 text-rose-700 hover:bg-rose-50 font-semibold px-4 py-2 text-sm">Mijn versie opslaan (overschrijft die van {conflict.by || "collega"})</button>
        </div>
      </div>
    </div>
  );
}

export function HistoryModal({ show, history, onClose, onRestore, canRestore = true }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="history-modal">
      <div className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2 font-bold text-slate-800"><History className="h-5 w-5 text-[#1C64B0]" /> Wijzigingshistorie</div>
          <button data-testid="history-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-sm font-semibold">Sluiten</button>
        </div>
        <div className="overflow-y-auto p-4 space-y-3">
          {history.length === 0 ? <p className="text-center text-slate-400 py-8 text-sm">Nog geen opgeslagen versies.</p>
            : history.map((v, i) => (
              <div key={v.id} className="rounded-md border border-slate-200 p-3" data-testid="history-item">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{v.updated_at ? new Date(v.updated_at).toLocaleString("nl-NL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—"}{i === 0 && <span className="ml-2 text-[10px] font-bold uppercase text-emerald-600">huidige</span>}</div>
                    <div className="text-xs text-slate-500">door {v.updated_by || "onbekend"}</div>
                  </div>
                  {i !== 0 && canRestore && <button data-testid="history-restore" onClick={() => onRestore(v.id)} className="shrink-0 rounded-md border border-[#1C64B0] text-[#1C64B0] hover:bg-blue-50 font-semibold px-3 py-1.5 text-xs">Terugzetten</button>}
                </div>
                <ul className="mt-2 space-y-0.5 text-xs text-slate-600 list-disc list-inside">
                  {(v.summary || []).map((s, j) => <li key={j}>{s}</li>)}
                </ul>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

export function LegacyMapModal({ show, legacyNames, mapDraft, setMapDraft, products, onClose, onApply, btn }) {
  if (!show) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="legacy-map-modal">
      <div className="w-full max-w-2xl max-h-[80vh] flex flex-col rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="font-bold text-slate-800">Oude productieregels koppelen</div>
          <button data-testid="legacy-map-close" onClick={onClose} className="text-slate-400 hover:text-slate-700 text-sm font-semibold">Sluiten</button>
        </div>
        <div className="overflow-y-auto p-4 space-y-3">
          <p className="text-sm text-slate-500">Kies per oude productnaam het bijpassende nieuwe artikel. De voorgestelde match staat al geselecteerd. Kies "-" om niet te koppelen (oude naam blijft staan).</p>
          {legacyNames.map((n) => (
            <div key={n} className="grid grid-cols-1 md:grid-cols-2 gap-2 items-center" data-testid="legacy-map-row">
              <div className="text-sm font-semibold text-slate-700">{n}</div>
              <SelectInput testid="legacy-map-select" value={mapDraft[n] || ""} onChange={(v) => setMapDraft({ ...mapDraft, [n]: v })} options={["", ...products.map((p) => p.naam)]} />
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200">
          <button data-testid="legacy-map-cancel" onClick={onClose} className="rounded-md border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Annuleren</button>
          <button data-testid="legacy-map-apply" onClick={onApply} className={btn}>Koppelen toepassen</button>
        </div>
      </div>
    </div>
  );
}
